import { useSaveUserProfile } from '@actions';
import { ReactComponent as ExamIcon } from '@assets/icons/profile-icons/exam-icon.svg';
import Input from '@components/V4/Inputs/Input';
import TextArea from '@components/V4/Inputs/Textarea';
import { zodResolver } from '@hookform/resolvers/zod';
import { UserExamWithName } from '@shared/clientTypes/DTOs/UserDTO';
import { useAnalytics } from 'apps/agora/src/contexts/AnalyticsContext';
import useToast from 'apps/agora/src/hooks/useToast';
import { ANALYTICS_EVENT_NAMES } from 'apps/agora/src/utils/constants';
import { useContext } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { z } from 'zod';
import { ProfileContext } from '../../../AccountProfile';
import { ExamFormProps } from '../../../types';
import StudentFormWrapper from '../../ProfileFormWrapper';

interface LogProgressFormProps extends ExamFormProps {
  examData?: UserExamWithName;
  setIsLogProgress: (value: React.SetStateAction<boolean>) => void;
}

const formDataSchema = z.object({
  prevGrade: z.string().min(1, { message: 'Please enter a valid score.' }).default(''),
  areasOfImprovement: z.string().optional().default(''),
});

type FormData = z.infer<typeof formDataSchema>;

const LogProgressForm = (props: LogProgressFormProps) => {
  const {
    isModeAddNew,
    userId,
    contentClassName,
    className,
    examData,
    setIsLogProgress,
    onCancel,
  } = props;

  const { isExternalViewer } = useContext(ProfileContext);

  const toast = useToast();
  const query = useQueryClient();
  const { trackEvent } = useAnalytics();

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    getValues,
    formState: { errors, touchedFields },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(formDataSchema),
    reValidateMode: 'onBlur',
    mode: 'onBlur',
  });

  const editButtonHandler = () => {
    setIsLogProgress?.(false);
    reset();
  };

  const { mutate: updateUserProfile, isLoading } = useSaveUserProfile(userId, {
    onSuccess: async () => {
      trackEvent(
        isExternalViewer
          ? ANALYTICS_EVENT_NAMES.STUDENT_PROFILE_CHANGE_BY_MENTOR
          : ANALYTICS_EVENT_NAMES.PROFILE_CHANGE
      );

      toast.success('Successfully updated your profile.');
      await query.invalidateQueries('/users/:id/profile');
      editButtonHandler();
    },
    onError: () => {
      toast.error('Profile could not be saved.');
    },
  });

  const onSubmit = async (data: FormData) => {
    const isValid = await trigger();
    if (isValid && examData?._id) {
      const exam = {
        ...examData,
        ...data,
        ...(examData?.newExam?._id && {
          exam: undefined,
          newExam: examData.newExam._id,
        }),
        _id: examData?._id,
      };

      updateUserProfile({
        exams: [exam],
      });
    }
  };

  const touchAllFields = (fields: Record<keyof FormData, string>) => {
    Object.keys(fields).forEach((key) => {
      setValue(key as keyof FormData, getValues()[key as keyof FormData], {
        shouldTouch: true,
        shouldValidate: true,
      });
    });
  };

  const submitHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
    const formData = getValues();

    touchAllFields(formData);

    handleSubmit(onSubmit)(e);
  };

  return (
    <StudentFormWrapper
      title="Progress"
      isModeAddNew={isModeAddNew}
      onCancel={onCancel}
      className={className}
      contentClassName={contentClassName}
      submitHandler={submitHandler}
      isLoading={isLoading}
      editButtonHandler={editButtonHandler}
      icon={isModeAddNew && <ExamIcon className="min-w-5 w-5" />}
    >
      <Input
        type="text"
        id={'prevGrade'}
        placeholder={''}
        label={'Last Score'}
        isTouched={touchedFields.prevGrade}
        isValid={!errors.prevGrade}
        errorText={errors.prevGrade?.message}
        isRequired
        autoFocus
        {...register('prevGrade')}
      />

      <TextArea
        id={'areasOfImprovement'}
        placeholder={''}
        className="hover:bg-opacity-50 focus:bg-opacity-50"
        label={'Ares Of Improvement'}
        {...register('areasOfImprovement')}
      />
    </StudentFormWrapper>
  );
};

export default LogProgressForm;
